import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle,
  GitMerge,
  Lightbulb,
  Trophy,
  UserPlus,
  X,
} from '@phosphor-icons/react';

import {
  fixSubject,
  getSuggestionDetail,
  reevaluateSuggestion,
  type DetailRow,
} from '@/api/automation';
import { approveCommand } from '@/api/commands';
import { listMembers, type Member } from '@/api/members';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { FormSkeleton } from '@/components/ui/form-skeleton';
import { Input } from '@/components/ui/input';
import { SearchInput } from '@/components/ui/search-input';
import { useConfirm } from '@/components/ConfirmDialog';
import { MemberCard } from '@/components/MemberCard';
import { useToast } from '@/components/Toast';
import { cn } from '@/lib/utils';
import { resolve as resolveTeamColor } from '@/lib/teamColor';

// The duplicate-pair extra payload the backend writes onto each merge row.
// Shared between the card preview and the merge picker so both renderers
// agree on the shape.
interface DuplicatePairExtra {
  other_id: string;
  other_name: string;
  other_email?: string;
  other_birth_date?: string;
  match_reason: 'email' | 'name_dob' | string;
}

// The extra payload on a played-match-without-a-score row. The side labels come
// from the server already ordered home-first, because two bare numbers are
// meaningless until the UI can say which one is ours — is_home decides which
// input the club's own goals belong in, and getting it backwards would publish an
// inverted result to the public match centre.
interface FixtureScoreExtra {
  is_home: boolean;
  home_label: string;
  away_label: string;
  played_on: string;
  our_side: 'home' | 'away' | string;
  opponent: string;
  match_title: string;
}

// AssistantDetail — per-suggestion detail view. Lists the affected entities
// the underlying check returned via DetailedCheck.Details() and renders the
// right fix widget per fix_action_kind. The detail page is the "what's
// actually wrong + let me fix it inline" surface the row-level overview can't
// fit.
//
// Wiring per fix_action_kind:
//   assign_leader → MemberLeaderPicker (search + select)
//   record_score  → RecordScorePicker (two labelled score inputs)
//   merge / dismiss / edit / navigate → guidance text only for now (each one
//   ships its widget as the corresponding fix materializer lands)
export function AssistantDetail() {
  const { slug = '', suggestionID = '' } = useParams();
  const { t } = useTranslation('common');
  const auth = useAuth();
  const token = auth.user?.access_token ?? null;
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Inline MemberCard sheet — when a SubjectMember row's Öppna is clicked
  // we set memberCardID and open the sheet here, instead of navigating
  // away to /members. Lets the admin add the missing email / fix profile
  // data without losing their place in the assistant list. Same component
  // the Members page uses; it owns its own save flow through CommandIntent.
  const [memberCardID, setMemberCardID] = useState<string | null>(null);
  const memberCardOpen = memberCardID !== null;
  const handleMemberCardOpenChange = (open: boolean) => {
    if (!open) {
      setMemberCardID(null);
      // The sheet's save flow can change the underlying member (e.g. add the
      // email that put them on this list), so re-check the condition server-side
      // before refetching — otherwise the detail row drops off but the SUGGESTION
      // itself stays open in the list until the nightly sweep, and the user is
      // told to fix something they just fixed.
      void reevaluateSuggestion(token, slug, suggestionID)
        .catch(() => undefined)
        .finally(() => {
          void queryClient.invalidateQueries({
            queryKey: ['assistant-detail', slug, suggestionID],
          });
          void queryClient.invalidateQueries({
            queryKey: ['assistant-suggestions', 'open', slug],
          });
        });
    }
  };

  const detailQuery = useQuery({
    queryKey: ['assistant-detail', slug, suggestionID],
    queryFn: () => getSuggestionDetail(token, slug, suggestionID),
    enabled: !!token && !!slug && !!suggestionID,
  });

  // Mirror the i18n strategy from Assistant.tsx — translate title/summary
  // from the check_key so the user's locale wins over the (English)
  // server-rendered text the sweep wrote. Pluralization for the title
  // count uses the same `title_one` / `title_other` keys the list page uses.
  const suggestion = detailQuery.data?.suggestion;
  const localizedTitle = suggestion?.check_key
    ? t(`assistant.checks.${suggestion.check_key}.title`, {
        count: suggestion.blast_radius.row_count,
        defaultValue: suggestion.title,
      })
    : (suggestion?.title ?? t('assistant.detail.loadingTitle'));
  const localizedSummary = suggestion?.check_key
    ? t(`assistant.checks.${suggestion.check_key}.summary`, {
        defaultValue: suggestion.summary,
      })
    : suggestion?.summary;

  return (
    <section className="mx-auto w-full max-w-[var(--content-max)] px-4 py-6 sm:px-8 sm:py-8">
      <header className="mb-6 flex flex-col gap-3">
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate(`/orgs/${slug}/assistant`)}
          className="self-start"
        >
          <ArrowLeft className="h-4 w-4" />
          {t('assistant.detail.back')}
        </Button>
        <div className="flex items-center gap-2">
          <Lightbulb weight="bold" className="h-6 w-6 text-primary" />
          <h1 className="font-heading text-2xl font-bold tracking-tight sm:text-3xl">
            {localizedTitle}
          </h1>
        </div>
        {localizedSummary && (
          <p className="text-sm text-muted-foreground">{localizedSummary}</p>
        )}
      </header>

      {detailQuery.isLoading && <FormSkeleton rows={4} action={false} />}
      {detailQuery.isError && (
        <p role="alert" className="text-sm text-destructive">
          {t('assistant.loadError')}
        </p>
      )}
      {detailQuery.data && (
        <DetailRows
          slug={slug}
          token={token}
          suggestionID={suggestionID}
          checkKey={detailQuery.data.check_key}
          hasFix={detailQuery.data.has_fix}
          rows={detailQuery.data.rows}
          onOpenMember={setMemberCardID}
        />
      )}
      <MemberCard
        open={memberCardOpen}
        onOpenChange={handleMemberCardOpenChange}
        slug={slug}
        memberID={memberCardID}
        onOpenMember={setMemberCardID}
      />
    </section>
  );
}

interface DetailRowsProps {
  slug: string;
  token: string | null;
  suggestionID: string;
  checkKey: string;
  hasFix: boolean;
  rows: DetailRow[];
  // Called when a SubjectMember row's Öppna is clicked. The page mounts the
  // shared MemberCard sheet — we don't navigate away from the assistant
  // detail so the admin can fix the data and immediately see the row drop.
  onOpenMember: (memberID: string) => void;
}

function DetailRows({
  slug,
  token,
  suggestionID,
  checkKey,
  hasFix,
  rows,
  onOpenMember,
}: DetailRowsProps) {
  const { t } = useTranslation('common');
  if (rows.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-border p-8 text-center">
        <p className="font-heading text-lg font-semibold text-foreground">
          {t('assistant.detail.resolved.title')}
        </p>
        <p className="mt-1 text-sm text-muted-foreground">
          {t('assistant.detail.resolved.body')}
        </p>
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-1.5">
      {rows.map((row, idx) => (
        <DetailRowCard
          // A subject_id can repeat across rows — for merge checks the same
          // member shows up in more than one duplicate pair, so the row's real
          // identity is the (subject_id, other_id) pair. Compose both, with the
          // index as a final tiebreaker for any future check that emits the
          // same subject twice without an other_id.
          key={`${row.subject_id}:${(row.extra as DuplicatePairExtra | undefined)?.other_id ?? idx}`}
          slug={slug}
          token={token}
          suggestionID={suggestionID}
          checkKey={checkKey}
          hasFix={hasFix}
          row={row}
          onOpenMember={onOpenMember}
        />
      ))}
    </div>
  );
}

interface DetailRowCardProps {
  slug: string;
  token: string | null;
  suggestionID: string;
  checkKey: string;
  hasFix: boolean;
  row: DetailRow;
  onOpenMember: (memberID: string) => void;
}

function DetailRowCard({
  slug,
  token,
  suggestionID,
  checkKey,
  hasFix,
  row,
  onOpenMember,
}: DetailRowCardProps) {
  const { t } = useTranslation('common');
  // Open/close picker panel state. Lives on the row so multiple rows can be
  // expanded independently — admins comparing teams want to see context
  // side-by-side, not collapse one to open another.
  const [pickerOpen, setPickerOpen] = useState(false);

  // Merge rows carry the OTHER candidate in extra. Surface it on the card
  // body (not just inside the picker) so the user can read the pair at a
  // glance — "Same email" without showing whose email leads to the obvious
  // "same email as whom?" question.
  const pair = row.fix_action_kind === 'merge'
    ? (row.extra as DuplicatePairExtra | undefined)
    : undefined;
  const pairSubtitle = pair?.other_email
    || (pair?.other_birth_date ? `DOB ${pair.other_birth_date}` : '');

  // Why is server-rendered for most checks (English fallback). For merges
  // we translate from match_reason so the badge follows the user's locale.
  const why = pair
    ? t(`assistant.detail.fix.merge.reason.${pair.match_reason}`, {
        defaultValue: t('assistant.detail.fix.merge.reason.unknown'),
      })
    : row.why || t(`assistant.checks.${checkKey}.rowWhy`, { defaultValue: '' });

  // Member rows open the inline MemberCard sheet instead of navigating; team
  // / activity rows keep the existing "go to the entity's page" behavior.
  const isMemberRow = row.subject_kind === 'member';
  const handleOpen = () => {
    if (isMemberRow) onOpenMember(row.subject_id);
  };

  return (
    <div className="rounded-xl border border-border bg-card px-3 py-2">
      <div className="flex items-center gap-3">
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <span className="truncate text-sm font-medium text-foreground">
            {row.title}
          </span>
          {row.subtitle && (
            <span className="hidden truncate text-xs text-muted-foreground sm:inline">
              {row.subtitle}
            </span>
          )}
          {pair && (
            <span className="hidden items-center gap-1 truncate text-xs text-muted-foreground sm:flex">
              <GitMerge weight="bold" className="h-3 w-3 shrink-0" />
              <span className="truncate">
                {pair.other_name}
                {pairSubtitle ? ` · ${pairSubtitle}` : ''}
              </span>
            </span>
          )}
          {why && (
            <Badge variant="outline" className="hidden shrink-0 text-[10px] sm:inline-flex">
              {why}
            </Badge>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {row.navigate && !isMemberRow && (
            <Button asChild variant="outline" size="sm" className="h-7 px-2 text-xs">
              <Link to={`/orgs/${slug}${row.navigate}`}>
                {t('assistant.detail.openSubject')}
                <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </Button>
          )}
          {isMemberRow && (
            <Button
              variant="outline"
              size="sm"
              className="h-7 px-2 text-xs"
              onClick={handleOpen}
            >
              {t('assistant.detail.openSubject')}
              <ArrowRight className="h-3.5 w-3.5" />
            </Button>
          )}
          {hasFix && row.fix_action_kind === 'assign_leader' && (
            <Button
              variant={pickerOpen ? 'outline' : 'default'}
              size="sm"
              className="h-7 px-2 text-xs"
              onClick={() => setPickerOpen((v) => !v)}
            >
              {pickerOpen ? (
                <>
                  <X className="h-3.5 w-3.5" />
                  {t('assistant.detail.cancel')}
                </>
              ) : (
                <>
                  <UserPlus className="h-3.5 w-3.5" />
                  {t('assistant.detail.fix.assignLeader.cta')}
                </>
              )}
            </Button>
          )}
          {hasFix && row.fix_action_kind === 'merge' && (
            <Button
              variant={pickerOpen ? 'outline' : 'default'}
              size="sm"
              className="h-7 px-2 text-xs"
              onClick={() => setPickerOpen((v) => !v)}
            >
              {pickerOpen ? (
                <>
                  <X className="h-3.5 w-3.5" />
                  {t('assistant.detail.cancel')}
                </>
              ) : (
                <>
                  <GitMerge className="h-3.5 w-3.5" />
                  {t('assistant.detail.fix.merge.cta')}
                </>
              )}
            </Button>
          )}
          {hasFix && row.fix_action_kind === 'record_score' && (
            <Button
              variant={pickerOpen ? 'outline' : 'default'}
              size="sm"
              className="h-7 px-2 text-xs"
              onClick={() => setPickerOpen((v) => !v)}
            >
              {pickerOpen ? (
                <>
                  <X className="h-3.5 w-3.5" />
                  {t('assistant.detail.cancel')}
                </>
              ) : (
                <>
                  <Trophy className="h-3.5 w-3.5" />
                  {t('assistant.detail.fix.recordScore.cta')}
                </>
              )}
            </Button>
          )}
        </div>
      </div>

      {/* Mobile-only second line: surface the subtitle/pair/why when the
          single-line layout truncates them off-screen. Mirrors the desktop
          inline strip so the row stays scannable on narrow viewports. */}
      {(row.subtitle || pair || why) && (
        <div className="mt-1 flex flex-wrap items-center gap-2 sm:hidden">
          {row.subtitle && (
            <span className="text-xs text-muted-foreground">{row.subtitle}</span>
          )}
          {pair && (
            <span className="flex items-center gap-1 text-xs text-muted-foreground">
              <GitMerge weight="bold" className="h-3 w-3" />
              {pair.other_name}
              {pairSubtitle ? ` · ${pairSubtitle}` : ''}
            </span>
          )}
          {why && (
            <Badge variant="outline" className="text-[10px]">
              {why}
            </Badge>
          )}
        </div>
      )}

      {pickerOpen && hasFix && row.fix_action_kind === 'assign_leader' && (
        <div className="mt-3 border-t border-border pt-3">
          <AssignLeaderPicker
            slug={slug}
            token={token}
            suggestionID={suggestionID}
            row={row}
            onDone={() => setPickerOpen(false)}
          />
        </div>
      )}
      {pickerOpen && hasFix && row.fix_action_kind === 'merge' && (
        <div className="mt-3 border-t border-border pt-3">
          <MergePairPicker
            slug={slug}
            token={token}
            suggestionID={suggestionID}
            row={row}
            onDone={() => setPickerOpen(false)}
          />
        </div>
      )}
      {pickerOpen && hasFix && row.fix_action_kind === 'record_score' && (
        <div className="mt-3 border-t border-border pt-3">
          <RecordScorePicker
            slug={slug}
            token={token}
            suggestionID={suggestionID}
            row={row}
            onDone={() => setPickerOpen(false)}
          />
        </div>
      )}
    </div>
  );
}

interface AssignLeaderPickerProps {
  slug: string;
  token: string | null;
  suggestionID: string;
  row: DetailRow;
  onDone: () => void;
}

function AssignLeaderPicker({
  slug,
  token,
  suggestionID,
  row,
  onDone,
}: AssignLeaderPickerProps) {
  const { t } = useTranslation('common');
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const toast = useToast();
  const [search, setSearch] = useState('');

  const membersQuery = useQuery({
    queryKey: ['assistant-detail-members', slug, search],
    // includeTeams powers the per-member team badges AND lets the search box
    // match team names (so typing a team surfaces its members).
    queryFn: () => listMembers(token, slug, { search, limit: 25, includeTeams: true }),
    enabled: !!token && !!slug,
  });

  // The fix flow matches the existing typed-Operation one-shot pattern:
  // POST /fix creates the pending CommandIntent (one DB write, no execute),
  // POST /commands/{id}/approve executes it under the same user auth.
  // Showing useConfirm between the two calls keeps the design-system dialog
  // as the single moment-of-truth before any mutation lands.
  const fixMutation = useMutation({
    mutationFn: async (memberID: string) => {
      const res = await fixSubject(token, slug, suggestionID, {
        subject_id: row.subject_id,
        args: { member_id: memberID },
      });
      await approveCommand(token, slug, res.intent_id);
      // The mutation has landed, so ask the server to re-check the condition and
      // close (or re-count) the suggestion. Best-effort: the fix itself already
      // succeeded, and the nightly sweep is the backstop, so a failure here must
      // not surface as "your fix failed".
      await reevaluateSuggestion(token, slug, suggestionID).catch(() => undefined);
    },
    onSuccess: () => {
      toast.success(t('assistant.detail.fix.assignLeader.success'));
      void queryClient.invalidateQueries({
        queryKey: ['assistant-detail', slug, suggestionID],
      });
      void queryClient.invalidateQueries({
        queryKey: ['assistant-suggestions', 'open', slug],
      });
      onDone();
    },
    onError: (err) => {
      const msg = err instanceof Error ? err.message : String(err);
      // already_resolved means someone else fixed this team while the page
      // was open. Treat as a benign refresh, not an error toast.
      if (msg.includes('already_resolved') || msg.includes('already_decided')) {
        toast.success(t('assistant.detail.fix.alreadyResolved'));
        void queryClient.invalidateQueries({
          queryKey: ['assistant-detail', slug, suggestionID],
        });
        onDone();
        return;
      }
      toast.error(t('assistant.detail.fix.failure'));
    },
  });

  const handlePick = async (member: Member) => {
    const fullName = `${member.first_name} ${member.last_name}`.trim() || member.email || '—';
    const ok = await confirm({
      title: t('assistant.detail.fix.assignLeader.confirmTitle'),
      body: t('assistant.detail.fix.assignLeader.confirmBody', {
        member: fullName,
        team: row.title,
      }),
      confirmLabel: t('assistant.detail.fix.assignLeader.confirmCta'),
    });
    if (ok) {
      fixMutation.mutate(member.id);
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <label className="flex flex-col gap-1.5 text-sm font-medium text-foreground">
        {t('assistant.detail.fix.assignLeader.searchLabel')}
        <SearchInput
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('assistant.detail.fix.assignLeader.searchPlaceholder')}
          clearLabel={t('common.clearSearch')}
          autoFocus
        />
      </label>
      <div className="max-h-64 overflow-y-auto rounded-xl border border-border">
        {membersQuery.isLoading && (
          <p className="px-3 py-2 text-sm text-muted-foreground">{t('common.loading')}</p>
        )}
        {membersQuery.isSuccess && membersQuery.data.members.length === 0 && (
          <p className="px-3 py-2 text-sm text-muted-foreground">
            {t('assistant.detail.fix.assignLeader.empty')}
          </p>
        )}
        {membersQuery.data?.members.map((m) => {
          const fullName = `${m.first_name} ${m.last_name}`.trim();
          return (
            <button
              key={m.id}
              type="button"
              onClick={() => handlePick(m)}
              disabled={fixMutation.isPending}
              className={cn(
                'flex w-full items-center justify-between gap-3 border-b border-border px-3 py-2 text-left transition-colors last:border-b-0 hover:bg-muted/50 disabled:opacity-50',
              )}
            >
              <span className="flex min-w-0 flex-col gap-1">
                <span className="text-sm font-medium text-foreground">{fullName || '—'}</span>
                <span className="text-xs text-muted-foreground">{m.email || '—'}</span>
                {m.teams && m.teams.length > 0 && (
                  <span className="flex flex-wrap gap-1">
                    {m.teams.map((team) => {
                      const palette = resolveTeamColor(team.name, team.color);
                      return (
                        <span
                          key={team.id}
                          className="inline-flex items-center gap-1 rounded-pill px-2 py-0.5 text-[11px] font-medium text-foreground"
                          style={{ backgroundColor: palette.bg }}
                        >
                          <span
                            aria-hidden="true"
                            className="h-1.5 w-1.5 shrink-0 rounded-full"
                            style={{ backgroundColor: palette.dot }}
                          />
                          {team.name}
                        </span>
                      );
                    })}
                  </span>
                )}
              </span>
              <CheckCircle weight="bold" className="h-4 w-4 shrink-0 text-muted-foreground" />
            </button>
          );
        })}
      </div>
    </div>
  );
}

interface MergePairPickerProps {
  slug: string;
  token: string | null;
  suggestionID: string;
  row: DetailRow;
  onDone: () => void;
}

// MergePairPicker renders the two candidates side by side and lets the user
// pick which member to KEEP. The other side becomes the duplicate (soft-
// deleted by the merge op). The keeper choice is the only decision — the
// backend verifies both IDs come from the original pair via the
// SubjectID-must-match-one-of-them guard.
function MergePairPicker({
  slug,
  token,
  suggestionID,
  row,
  onDone,
}: MergePairPickerProps) {
  const { t } = useTranslation('common');
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const toast = useToast();
  const extra = (row.extra as DuplicatePairExtra | undefined) ?? null;

  const fixMutation = useMutation({
    mutationFn: async (keeperID: string) => {
      if (!extra) throw new Error('missing duplicate pair payload');
      const res = await fixSubject(token, slug, suggestionID, {
        subject_id: row.subject_id,
        args: { keeper_id: keeperID, other_id: extra.other_id },
      });
      await approveCommand(token, slug, res.intent_id);
      // The mutation has landed, so ask the server to re-check the condition and
      // close (or re-count) the suggestion. Best-effort: the fix itself already
      // succeeded, and the nightly sweep is the backstop, so a failure here must
      // not surface as "your fix failed".
      await reevaluateSuggestion(token, slug, suggestionID).catch(() => undefined);
    },
    onSuccess: () => {
      toast.success(t('assistant.detail.fix.merge.success'));
      void queryClient.invalidateQueries({
        queryKey: ['assistant-detail', slug, suggestionID],
      });
      void queryClient.invalidateQueries({
        queryKey: ['assistant-suggestions', 'open', slug],
      });
      onDone();
    },
    onError: (err) => {
      const msg = err instanceof Error ? err.message : String(err);
      if (msg.includes('already_resolved') || msg.includes('already_decided')) {
        toast.success(t('assistant.detail.fix.alreadyResolved'));
        void queryClient.invalidateQueries({
          queryKey: ['assistant-detail', slug, suggestionID],
        });
        onDone();
        return;
      }
      toast.error(t('assistant.detail.fix.failure'));
    },
  });

  if (!extra) {
    return (
      <p className="text-sm text-destructive">
        {t('assistant.detail.fix.merge.missingPair')}
      </p>
    );
  }

  const handlePick = async (keeperID: string, keeperLabel: string, dropLabel: string) => {
    const ok = await confirm({
      title: t('assistant.detail.fix.merge.confirmTitle'),
      body: t('assistant.detail.fix.merge.confirmBody', {
        keep: keeperLabel,
        drop: dropLabel,
      }),
      confirmLabel: t('assistant.detail.fix.merge.confirmCta'),
      tone: 'destructive',
    });
    if (ok) {
      fixMutation.mutate(keeperID);
    }
  };

  const leftLabel = `${row.title}${row.subtitle ? ` — ${row.subtitle}` : ''}`;
  const rightSubtitle = extra.other_email || (extra.other_birth_date ? `DOB ${extra.other_birth_date}` : '');
  const rightLabel = `${extra.other_name}${rightSubtitle ? ` — ${rightSubtitle}` : ''}`;

  return (
    <div className="flex flex-col gap-3">
      <p className="text-sm text-muted-foreground">
        {t('assistant.detail.fix.merge.instruction')}
      </p>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <MergeCandidateCard
          name={row.title}
          subtitle={row.subtitle ?? ''}
          disabled={fixMutation.isPending}
          onPick={() => handlePick(row.subject_id, leftLabel, rightLabel)}
          cta={t('assistant.detail.fix.merge.keepThis')}
        />
        <MergeCandidateCard
          name={extra.other_name}
          subtitle={rightSubtitle}
          disabled={fixMutation.isPending}
          onPick={() => handlePick(extra.other_id, rightLabel, leftLabel)}
          cta={t('assistant.detail.fix.merge.keepThis')}
        />
      </div>
    </div>
  );
}

function MergeCandidateCard({
  name,
  subtitle,
  disabled,
  onPick,
  cta,
}: {
  name: string;
  subtitle: string;
  disabled: boolean;
  onPick: () => void;
  cta: string;
}) {
  return (
    <div className="flex flex-col gap-3 rounded-xl border border-border p-3">
      <div className="flex flex-col gap-0.5">
        <span className="text-sm font-medium text-foreground">{name}</span>
        {subtitle && <span className="text-xs text-muted-foreground">{subtitle}</span>}
      </div>
      <Button variant="default" size="sm" onClick={onPick} disabled={disabled}>
        <CheckCircle className="h-4 w-4" />
        {cta}
      </Button>
    </div>
  );
}

// RecordScorePicker — enter a played match's score without leaving the
// assistant. Before this existed, the fixture check said "1 played match has no
// result" and its only action was a jump to the calendar, which told the admin
// neither WHICH match nor let them do anything about it in place.
//
// Home-first, always, with both club names printed above their own input. The
// server sends the labels already ordered and tells us which side is ours, so the
// UI never has to infer it — an inverted score here would publish the wrong
// result to the public match centre.
function RecordScorePicker({
  slug,
  token,
  suggestionID,
  row,
  onDone,
}: AssignLeaderPickerProps) {
  const { t } = useTranslation('common');
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const toast = useToast();

  const extra = (row.extra ?? null) as FixtureScoreExtra | null;
  // Strings, not numbers, so the field can be genuinely empty. Defaulting to 0
  // would let an accidental "save" record a 0–0 that reads as a real draw.
  const [home, setHome] = useState('');
  const [away, setAway] = useState('');

  const parsed = (v: string): number | null => {
    if (v.trim() === '') return null;
    const n = Number(v);
    return Number.isInteger(n) && n >= 0 && n <= 999 ? n : null;
  };
  const homeScore = parsed(home);
  const awayScore = parsed(away);
  const valid = homeScore !== null && awayScore !== null;

  const fixMutation = useMutation({
    mutationFn: async (vars: { home: number; away: number }) => {
      const res = await fixSubject(token, slug, suggestionID, {
        subject_id: row.subject_id,
        args: { home_score: vars.home, away_score: vars.away },
      });
      await approveCommand(token, slug, res.intent_id);
      // The mutation has landed, so ask the server to re-check the condition and
      // close (or re-count) the suggestion. Best-effort: the fix itself already
      // succeeded, and the nightly sweep is the backstop, so a failure here must
      // not surface as "your fix failed".
      await reevaluateSuggestion(token, slug, suggestionID).catch(() => undefined);
    },
    onSuccess: () => {
      toast.success(t('assistant.detail.fix.recordScore.success'));
      void queryClient.invalidateQueries({
        queryKey: ['assistant-detail', slug, suggestionID],
      });
      void queryClient.invalidateQueries({
        queryKey: ['assistant-suggestions', 'open', slug],
      });
      onDone();
    },
    onError: (err) => {
      const msg = err instanceof Error ? err.message : String(err);
      // Someone recorded this score while the page was open — a benign refresh,
      // not an error, and definitely not something to overwrite.
      if (msg.includes('already_resolved') || msg.includes('already_decided')) {
        toast.success(t('assistant.detail.fix.alreadyResolved'));
        void queryClient.invalidateQueries({
          queryKey: ['assistant-detail', slug, suggestionID],
        });
        onDone();
        return;
      }
      toast.error(t('assistant.detail.fix.failure'));
    },
  });

  const homeLabel = extra?.home_label ?? t('assistant.detail.fix.recordScore.home');
  const awayLabel = extra?.away_label ?? t('assistant.detail.fix.recordScore.away');

  const handleSave = async () => {
    if (homeScore === null || awayScore === null) return;
    const ok = await confirm({
      title: t('assistant.detail.fix.recordScore.confirmTitle'),
      // The confirm body spells the result out in full — "Malmö FF 3–1 IFK" —
      // so an inverted entry is caught here, while it's still cancellable.
      body: t('assistant.detail.fix.recordScore.confirmBody', {
        home: homeLabel,
        away: awayLabel,
        homeScore,
        awayScore,
      }),
      confirmLabel: t('assistant.detail.fix.recordScore.confirmCta'),
    });
    if (ok) {
      fixMutation.mutate({ home: homeScore, away: awayScore });
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-end gap-3">
        <label className="flex min-w-0 flex-1 flex-col gap-1.5 text-sm font-medium text-foreground">
          <span className="truncate">{homeLabel}</span>
          <Input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            value={home}
            onChange={(e) => setHome(e.target.value)}
            aria-label={homeLabel}
            className="w-20"
            autoFocus
          />
        </label>
        <span className="pb-2 text-lg font-semibold text-muted-foreground">–</span>
        <label className="flex min-w-0 flex-1 flex-col gap-1.5 text-sm font-medium text-foreground">
          <span className="truncate">{awayLabel}</span>
          <Input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            value={away}
            onChange={(e) => setAway(e.target.value)}
            aria-label={awayLabel}
            className="w-20"
          />
        </label>
      </div>
      <div className="flex items-center gap-2">
        <Button
          size="sm"
          disabled={!valid || fixMutation.isPending}
          onClick={() => void handleSave()}
        >
          {fixMutation.isPending
            ? t('common.saving')
            : t('assistant.detail.fix.recordScore.save')}
        </Button>
        <Button variant="outline" size="sm" onClick={onDone}>
          {t('assistant.detail.cancel')}
        </Button>
      </div>
    </div>
  );
}
